import {
  type ListTicketsQuery,
  type TicketPriority,
  TicketStatusGroup,
  propertyDateKey,
} from '@nbh/shared';

/**
 * The job dashboard's view state, and the two things derived from it: the query the
 * server is sent, and the key the answer is cached under. Pure, so the derivations are
 * unit-testable without a screen.
 *
 * Tabs and chips are orthogonal and compose with AND. A tab answers "what stage is
 * this job at"; a chip answers "which of those do I care about". Chips live outside
 * the tab on purpose — someone filtered to Urgent who taps Closed expects closed
 * urgent jobs, not a silently cleared filter.
 */

export type ClosedRangePreset = '7d' | '30d' | '90d' | 'custom';

export const CLOSED_RANGE_PRESETS: readonly ClosedRangePreset[] = ['7d', '30d', '90d', 'custom'];

/** Calendar days each preset spans, today included. */
const PRESET_DAYS: Record<Exclude<ClosedRangePreset, 'custom'>, number> = {
  '7d': 7,
  '30d': 30,
  '90d': 90,
};

/**
 * One page, and the schema's ceiling. There is no second page: the Closed archive is
 * bounded by its date range, and on the live tabs this is the safety net the brief
 * names. Hitting it is reported on screen, never paged past.
 */
export const QUEUE_PAGE_SIZE = 100;

export interface QueueChips {
  mine: boolean;
  priority: TicketPriority | null;
}

export interface ClosedRange {
  preset: ClosedRangePreset;
  /** Inclusive `YYYY-MM-DD` bounds, read only when the preset is `custom`. */
  from: string;
  to: string;
}

export interface QueueView {
  group: TicketStatusGroup;
  chips: QueueChips;
  range: ClosedRange;
}

export function clearedChips(): QueueChips {
  return { mine: false, priority: null };
}

export function hasChips(chips: QueueChips): boolean {
  return chips.mine || chips.priority !== null;
}

export function defaultQueueView(now: Date, timeZone: string): QueueView {
  const week = presetBounds('7d', now, timeZone);
  return {
    group: TicketStatusGroup.New,
    chips: clearedChips(),
    // The custom bounds start where the default preset does, so switching to Custom
    // shows the same window until a date is changed rather than an empty pair.
    range: { preset: '7d', from: week.from, to: week.to },
  };
}

/** `key` moved by `days` calendar days — arithmetic on the date, never on an instant. */
export function shiftDateKey(key: string, days: number): string {
  const [year, month, day] = key.split('-').map(Number) as [number, number, number];
  // Date.UTC normalises an out-of-range day, and there is no zone offset in UTC to push
  // the result across midnight — so this is exact across a DST week where 6 × 24h is not.
  return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

function presetBounds(
  preset: Exclude<ClosedRangePreset, 'custom'>,
  now: Date,
  timeZone: string,
): { from: string; to: string } {
  const to = propertyDateKey(now, timeZone);
  return { from: shiftDateKey(to, -(PRESET_DAYS[preset] - 1)), to };
}

/** The inclusive property-time bounds the Closed tab is asking for, resolved now. */
export function resolveRange(
  range: ClosedRange,
  now: Date,
  timeZone: string,
): { from: string; to: string } {
  if (range.preset !== 'custom') return presetBounds(range.preset, now, timeZone);

  const [from, to] = range.from <= range.to ? [range.from, range.to] : [range.to, range.from];
  return { from, to };
}

/**
 * What the server is asked. The date range travels only on Closed: the schema refuses it
 * anywhere else, and the live tabs must show an eight-day-old open job above everything.
 */
export function queryFor(view: QueueView, now: Date, timeZone: string): Partial<ListTicketsQuery> {
  return {
    group: view.group,
    mine: view.chips.mine || undefined,
    priority: view.chips.priority ?? undefined,
    ...(view.group === TicketStatusGroup.Closed ? resolveRange(view.range, now, timeZone) : {}),
    limit: QUEUE_PAGE_SIZE,
  };
}

/**
 * What the cached answer is filed under.
 *
 * Keyed on the *preset*, not the dates it resolved to: "last 7 days" fetched yesterday
 * is still the row a technician with no signal should see today, and a key carrying
 * yesterday's dates would never be looked up again. Every tab, with every chip
 * combination, gets its own row — so every tab a device has opened renders offline.
 */
export function cacheFilterFor(view: QueueView): Record<string, unknown> {
  return {
    group: view.group,
    mine: view.chips.mine || undefined,
    priority: view.chips.priority ?? undefined,
    range:
      view.group === TicketStatusGroup.Closed
        ? view.range.preset === 'custom'
          ? `${view.range.from}..${view.range.to}`
          : view.range.preset
        : undefined,
  };
}

/** Which sentence the empty state needs — one that names every active filter. */
export function emptyStateKey(chips: QueueChips): string {
  if (chips.mine && chips.priority !== null) return 'queue.empty.minePriority';
  if (chips.mine) return 'queue.empty.mine';
  if (chips.priority !== null) return 'queue.empty.priority';
  return 'queue.empty.none';
}
